<template>
	<div class="line-chart-container" ref="chartContainer">
		<svg ref="chart"></svg>
	</div>
</template>

<script setup lang="ts">
import { select, scaleTime, scaleLinear, extent, area, axisLeft, axisBottom, curveCardinal, line } from 'd3';
import { ref, onMounted, watch } from 'vue';
import { ChartProp } from './types';

const props = defineProps<{
	data: ChartProp[];
	formatX?: (xValue: number | Date, index?: number, data?: any[]) => string;
	formatY?: (yValue: number) => string;
	minY?: number;
	maxY?: number;
}>();

onMounted(() => {
	createChart();
});

watch(
	() => props.data,
	() => {
		select(chart.value).selectAll('*').remove();
		createChart();
	}
);

const chart = ref<SVGSVGElement | null>(null);
const chartContainer = ref<HTMLElement | null>(null);
const mouseover = function (d: MouseEvent) {
	(d.target as HTMLElement).setAttribute('r', '8');
	select('#tooltip-graph').style('opacity', 1);
};

const mouseleave = function (d: MouseEvent) {
	(d.target as HTMLElement).setAttribute('r', '6');
	select('#tooltip-graph').transition().duration(200).style('opacity', 0);
};

const mousemove = function (d: MouseEvent, index: number, dataProp: ChartProp['data']) {
	const data = (d.target as any)['__data__'] as { x: number; y: number; labelX: string };
	select('#tooltip-graph')
		.html(formatXaxis(data.x, index, dataProp) + ' - ' + (props.formatY ? props.formatY(data.y) : data.y))
		.style('opacity', 1)
		.style('left', d.pageX + 10 + 'px') // It is important to put the +90: other wise the tooltip is exactly where the point is an it creates a weird effect
		.style('top', d.pageY + 10 + 'px');
};

const formatXaxis = (x: number | Date, index?: number, data?: ChartProp['data']) => {
	return props.formatX
		? props.formatX(x, index, data)
		: new Intl.DateTimeFormat('en-US', {
				year: 'numeric',
				month: 'short',
		  }).format(x);
};

const defaultFormatY = (x: number) => {
	return new Intl.NumberFormat('en-US').format(x);
};

const defaultMaxY = (data: ChartProp['data']) => {
	const maxY = Math.floor(Math.max(...data.map(el => el.y as number)));
	const firstDigit = Math.floor(maxY / Math.pow(10, Math.floor(Math.log10(maxY))));
	return (firstDigit + 1) * Math.pow(10, Math.floor(Math.log10(maxY)));
};

const createChart = () => {
	const width = chartContainer.value?.offsetWidth as number;
	const showXAxisValues = showTickAllXAxisValues(width, props.data[0].data.length);
	const height = 400;
	const margin = { top: 10, right: 50, bottom: 40, left: 50 };

	const svg = select(chart.value).attr('width', width).attr('height', height);

	const data = props.data.map(el => el.data).flat();

	const isXaxisDate = typeof data[0].x != 'number';
	const xScale = isXaxisDate
		? scaleTime()
				.domain(extent(data, d => d.x as Date) as Date[])
				.range([margin.left, width - margin.right])
		: scaleLinear()
				.domain([
					Math.min(...data.map(el => el.x as number)) as number,
					Math.max(...data.map(el => el.x as number)) as number,
				])
				.range([margin.left, width - margin.right]);

	const maxYaxis = props.maxY || defaultMaxY(data);

	const yScale = scaleLinear()
		.domain([props.minY || 0, maxYaxis])
		.range([height - margin.bottom, margin.top]);

	const graphArea = area<{ x: number | Date; y: number }>()
		.x(d => xScale(d.x))
		.y0(height - margin.bottom)
		.y1(d => yScale(d.y))
		.curve(curveCardinal);

	// Add the y-axis
	svg.append('g')
		.attr('transform', `translate(${margin.left}, 0)`)
		.call(
			axisLeft(yScale).tickFormat(y => (props.formatY ? props.formatY(y as number) : defaultFormatY(y as number)))
		)
		.selectAll('text')
		.attr('class', 'yAxis-text');

	// Add the x-axis with custom tick format for dates
	svg.append('g')
		.attr('transform', `translate(0, ${height - margin.bottom})`)
		.call(
			isXaxisDate
				? axisBottom(xScale)
						// .tickValues([xScale.domain()[0], xScale.domain()[1]])
						.tickValues(showXAxisValues ? data.map(el => el.x) : [xScale.domain()[0], xScale.domain()[1]])
						.tickFormat((x, index) => formatXaxis(x as number | Date, index, data))
				: axisBottom(xScale)
						.tickValues(showXAxisValues ? data.map(el => el.x) : [xScale.domain()[0], xScale.domain()[1]])
						.tickFormat((x, index) =>
							props.formatX ? props.formatX(x as number | Date, index, data) : x.toString()
						)
		)
		.selectAll('text')
		.attr('class', 'xAxis-text');

	[...props.data]
		.sort((a, b) => Math.max(...b.data.map(el => el.y)) - Math.max(...a.data.map(el => el.y)))
		.forEach((el, index) => {
			const randomNumber = Math.random() * 1000;

			svg.append('linearGradient')
				.attr('id', `gradient-${randomNumber}-${index}`)
				.attr('gradientUnits', 'userSpaceOnUse')
				.attr('x1', 0)
				.attr('y1', yScale(0))
				.attr('x2', 0)
				.attr('y2', yScale(Math.max(...el.data.map(xy => xy.y))))
				.selectAll('stop')
				.data([
					{ offset: '0%', color: 'rgba(2, 62, 227, 0.00)' },
					{ offset: '100%', color: el.color },
				])
				.enter()
				.append('stop')
				.attr('offset', d => d.offset)
				.attr('stop-color', d => d.color);

			// Add the faded background for the blue graph

			svg.append('path')
				.datum(el.data)
				.attr('class', 'area')
				.attr('d', graphArea)
				.attr('fill', `url(#gradient-${randomNumber}-${index})`)
				.attr('fill-opacity', 0.3);

			// Add the line chart for the blue graph
			svg.append('path')
				.datum(el.data)
				.attr('fill', 'none')
				.attr('stroke', el.color)
				.attr('stroke-width', 2)
				.attr(
					'd',
					line<{ x: number | Date; y: number | Date }>()
						.x(d => xScale(d.x as Date))
						.y(d => yScale(d.y as number))
						.curve(curveCardinal)
				);

			// Add circles at each data point for the blue graph
			svg.selectAll(`circle${index}`)
				.data(el.data)
				.enter()
				.append('circle')
				.attr('class', `${index}`)
				.attr('cx', d => xScale(d.x as Date))
				.attr('cy', d => yScale(d.y as number))
				.attr('r', 5)
				.attr('fill', el.color)
				.style('cursor', 'pointer')
				.on('mouseover', mouseover)
				.on('mousemove', (e: MouseEvent) => mousemove(e, index, el.data))
				.on('mouseleave', mouseleave);
		});
};

const showTickAllXAxisValues = (width: number, xAxisTicksNumber: number) => {
	if (xAxisTicksNumber >= 10) {
		return width > 1100;
	} else {
		return width > 700;
	}
};
</script>

<style lang="scss" scoped>
.line-chart-container {
	width: 100%;
	display: block;
	:deep(.xAxis-text) {
		font-size: 14px;
	}
	:deep(.yAxis-text) {
		font-size: 14px;
	}
}
</style>
